import React from 'react';

import { useTranslation } from 'react-i18next';

type AlertVariant = 'error' | 'warning' | 'info' | 'success';

export interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children?: React.ReactNode;
  className?: string;
  onClose?: () => void;
}

const variantStyles: Record<
  AlertVariant,
  {
    container: string;
    title: string;
    icon: string;
    border: string;
  }
> = {
  error: {
    container: 'bg-red-50 dark:bg-red-950 text-red-800',
    title: 'text-red-900',
    icon: 'text-red-600',
    border: 'border-red-200 dark:border-red-700',
  },
  warning: {
    container: 'bg-yellow-50 dark:bg-yellow-950 text-yellow-800',
    title: 'text-yellow-900',
    icon: 'text-yellow-600',
    border: 'border-yellow-200 dark:border-yellow-700',
  },
  info: {
    container: 'bg-blue-50 dark:bg-blue-950 text-blue-800',
    title: 'text-blue-900',
    icon: 'text-blue-600',
    border: 'border-blue-200 dark:border-blue-700',
  },
  success: {
    container: 'bg-green-50 dark:bg-green-950 text-green-800',
    title: 'text-green-900',
    icon: 'text-green-600',
    border: 'border-green-200 dark:border-green-700',
  },
};

const VariantIcon: React.FC<{ variant: AlertVariant }> = ({ variant }) => {
  switch (variant) {
    case 'error':
      return (
        <svg
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16Zm-1-5a1 1 0 112 0 1 1 0 01-2 0Zm1-8a1 1 0 00-1 1v5a1 1 0 102 0V6a1 1 0 00-1-1Z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'warning':
      return (
        <svg
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.72-1.36 3.485 0l6.518 11.592c.75 1.335-.213 2.999-1.742 2.999H3.48c-1.53 0-2.492-1.664-1.743-2.999L8.257 3.1zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-7a1 1 0 00-1 1v3a1 1 0 102 0V8a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'success':
      return (
        <svg
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.172 7.707 8.879a1 1 0 10-1.414 1.414L9 13l4.707-4.707z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'info':
    default:
      return (
        <svg
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zM9 9a1 1 0 100-2 1 1 0 000 2zm1-2a1 1 0 110-2 1 1 0 010 2zm-2 6a1 1 0 112 0v2a1 1 0 11-2 0v-2z"
            clipRule="evenodd"
          />
        </svg>
      );
  }
};

export const Alert: React.FC<AlertProps> = ({
  variant = 'error',
  title,
  children,
  className = '',
  onClose,
}) => {
  const { t } = useTranslation('pleri/shared');
  const styles = variantStyles[variant];

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`flex items-start gap-3 rounded-md border ${styles.border} ${styles.container} p-3 shadow-sm ${className}`}
    >
      <div className={`${styles.icon} mt-0.5`}>
        <VariantIcon variant={variant} />
      </div>
      <div className="min-w-0 flex-1">
        {title && <div className={`font-medium ${styles.title}`}>{title}</div>}
        {children && <div className="mt-0.5 break-words">{children}</div>}
      </div>
      {onClose && (
        <button
          type="button"
          className="ml-2 rounded p-1 opacity-80 transition hover:bg-black/5 hover:opacity-100 focus:ring-2 focus:ring-black/10 focus:outline-none"
          aria-label={t('dismiss')}
          onClick={onClose}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 011.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default Alert;
